// Het vlak Meterkast (Spoor B, sessie B2; ADR 2026-09-10 §6 en §11).
//
// Eén meterlijst, drie vergelijkingen. Bovenaan kies je WAT je vergelijkt
// (periode · methode · run) en WELKE kant "huidig" is (methode, periode);
// daaronder staan de vijf borden als tabbladen, per bord een tabel met één rij
// per meter en één kolom per sector.
//
// De regel die hier het zwaarst weegt (zoals bij Kwaliteit, W6 ronde 5): dit
// scherm rekent niets na. Het verschil, het stoplicht en de reden komen al
// gevormd van de server (`sof.meterkast`); hier worden ze alleen getallen en
// woorden. Een percentage tekenen uit `waarde` is opmaak, geen oordeel — een
// eigen drempel of een eigen som zou dat wél zijn.
//
// Twee dingen die dit vlak bewust zelf doet:
//
//  1. De borden in één aanroep ophalen. De server stuurt alle vijf tegelijk;
//     per bord opnieuw vragen zou dezelfde rijen vijf keer over de lijn halen
//     en de tabbladen uit elkaar laten lopen.
//  2. Na een ijkpunt of een oordeel opnieuw ophalen. Het label en de uitslag
//     zijn die van de server, nooit een eigen aanname over wat er nu staat.

import { useEffect, useState } from "react";

import { Card } from "../../components/ui";
import { EmptyReason, LoadError } from "../../components/Empty";
import { api } from "../../lib/api";
import { useResource } from "../../lib/useResource";
import type { MeterkastResponse, MeterkastVergelijk } from "../../contracts";
import { color, radius, size, weight } from "../../tokens";
import { datum, noot } from "./BronnenStukken";
import { Bord } from "./MeterkastBord";
import { Ijkpunten } from "./MeterkastIjkpunt";
import { knopLicht } from "./stijl";
import { VERGELIJK_LABEL } from "./woorden";

const VERGELIJKINGEN: readonly MeterkastVergelijk[] = ["periode", "methode", "run"];

/** "aug. 2026" — een periode is altijd de eerste van de maand. */
export function maand(iso: string | null): string | null {
  if (!iso) return null;
  const d = datum(iso);
  if (!d) return null;
  // "1 aug. 2026" → "aug. 2026": de dag zegt niets, elke periode begint op de 1e.
  return d.replace(/^\d+\s/, "");
}

const keuze = {
  display: "inline-flex",
  flexWrap: "wrap",
  gap: 4,
  background: color.lineFaint,
  borderRadius: radius.input,
  padding: 3,
} as const;

export function Meterkast() {
  const [vergelijk, setVergelijk] = useState<MeterkastVergelijk>("periode");
  const [methode, setMethode] = useState<string | null>(null);
  const [period, setPeriod] = useState<string | null>(null);
  const [bord, setBord] = useState(1);

  const kast = useResource<MeterkastResponse>(
    () =>
      api.meterkast({
        vergelijk,
        methode: methode ?? undefined,
        period: period ?? undefined,
      }),
    [vergelijk, methode, period],
  );
  const k = kast.data;

  // Welk bord, zoals de schil het vlak meldt (W6, research §4): alleen WAAR
  // iemand kijkt, nooit wat erin staat. Vuur-en-vergeet.
  //
  // Met het WOORD van het bord, niet met zijn nummer: de sleutel in het
  // gebruikslog mag geen cijfers dragen (`beheer.schema.json#gebruikRequest`,
  // patroon ^[A-Za-z][A-Za-z &/-]{1,40}$). Een naam die dat patroon niet haalt
  // meldt alleen het vlak — liever een grovere telling dan een geweigerde.
  const bordNaam = k?.borden.find((b) => b.bord === bord)?.naam;
  useEffect(() => {
    const woord = logWoord(bordNaam);
    void api
      .gebruik({ scherm: woord ? `Beheer/Meterkast/${woord}` : "Beheer/Meterkast" })
      .catch(() => {});
  }, [bordNaam]);

  if (kast.error && !k) {
    return (
      <Card style={{ padding: 18 }}>
        <LoadError reason={kast.error} onRetry={kast.reload} />
      </Card>
    );
  }
  if (!k) {
    return (
      <Card style={{ padding: 18 }}>
        <EmptyReason reason="de meterkast wordt opgehaald" />
      </Card>
    );
  }

  const open = k.borden.find((b) => b.bord === bord) ?? k.borden[0];
  // Wat er staat is van de vorige selectie: licht gedimd, zoals de andere
  // beheervlakken (useResource.stale).
  const oud = { opacity: kast.stale ? 0.6 : 1, transition: "opacity 0.15s" } as const;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 22 }}>
      {kast.error && <NietVerversd reden={kast.error} onOpnieuw={kast.reload} />}

      <Schakelaar
        kast={k}
        vergelijk={vergelijk}
        methode={methode}
        period={period}
        onVergelijk={(v) => setVergelijk(v)}
        onMethode={(m) => setMethode(m)}
        onPeriod={(p) => setPeriod(p)}
      />

      <Ijkpunten kast={k} onGewijzigd={kast.reload} />

      <div style={oud}>
        {/* Hetzelfde gesegmenteerde patroon als de subnavigatie van Beheer. */}
        <nav aria-label="De vijf borden" style={{ ...keuze, marginBottom: 16 }}>
          {k.borden.map((b) => (
            <button
              key={b.bord}
              type="button"
              className={"view-btn" + (b.bord === open.bord ? " active" : "")}
              aria-pressed={b.bord === open.bord}
              onClick={() => setBord(b.bord)}
            >
              {`${b.bord}. ${bordKort(b.naam)}`}
            </button>
          ))}
        </nav>

        <Bord
          bord={open}
          sectoren={k.sectoren}
          vergelijk={k.vergelijk}
          huidig={k.huidig}
          basis={k.basis}
          onGewijzigd={kast.reload}
        />
      </div>

      {/* Eén zin, die van de server: hij zegt zelf al dat de drempels een
          voorstel zijn zolang ze niet geijkt zijn. Er nog een eigen zin achter
          plakken gaf hetzelfde twee keer (gezien op de testkaart). */}
      <p style={{ ...noot, marginTop: 0, ...oud }}>{k.drempelUitleg}</p>
    </div>
  );
}

/** "Oogst — het zoeken" → "Oogst". De volledige naam staat boven de tabel; op
 *  de knop past alleen het eerste woord, en met het bordnummer ervoor is dat
 *  genoeg om ze uit elkaar te houden. */
function bordKort(naam: string): string {
  return (naam.split("—")[0] ?? naam).trim();
}

/** Het woord waarmee een bord in het gebruikslog komt te staan, of null als de
 *  naam van de server het patroon van het contract niet haalt (cijfers, een
 *  gedachtestreepje, leestekens). Dan meldt het scherm alleen het vlak. */
export function logWoord(naam: string | undefined): string | null {
  if (!naam) return null;
  const woord = bordKort(naam);
  return /^[A-Za-z][A-Za-z -]{1,28}$/.test(woord) ? woord : null;
}

/** De keuzeschakelaar: wat vergelijk je, en welke kant is "huidig". De zin
 *  eronder zegt in gewone woorden welke twee standen je naast elkaar ziet —
 *  met de woorden van de server (`huidig`/`basis`), niet met een eigen
 *  afleiding uit de keuzes hierboven. */
function Schakelaar({
  kast,
  vergelijk,
  methode,
  period,
  onVergelijk,
  onMethode,
  onPeriod,
}: {
  kast: MeterkastResponse;
  vergelijk: MeterkastVergelijk;
  methode: string | null;
  period: string | null;
  onVergelijk: (v: MeterkastVergelijk) => void;
  onMethode: (m: string | null) => void;
  onPeriod: (p: string | null) => void;
}) {
  return (
    <Card style={{ padding: "16px 18px" }}>
      <div style={{ display: "flex", flexWrap: "wrap", gap: "14px 22px", alignItems: "flex-end" }}>
        <div>
          <div style={{ ...veldLabel, marginBottom: 7 }}>Wat vergelijk je?</div>
          <nav aria-label="Wat vergelijk je" style={keuze}>
            {VERGELIJKINGEN.map((v) => (
              <button
                key={v}
                type="button"
                className={"view-btn" + (v === vergelijk ? " active" : "")}
                aria-pressed={v === vergelijk}
                title={VERGELIJK_LABEL[v].vraag}
                onClick={() => onVergelijk(v)}
              >
                {VERGELIJK_LABEL[v].kort}
              </button>
            ))}
          </nav>
        </div>

        <Kies
          label="Periode"
          waarde={period ?? kast.huidig.period ?? ""}
          opties={opties(kast.periodes, kast.huidig.period, (p) => maand(p) ?? p)}
          onKies={(w) => onPeriod(w || null)}
        />
        <Kies
          label="Methode"
          waarde={methode ?? kast.huidig.methode}
          opties={opties(kast.methodes, kast.huidig.methode, (m) => m)}
          onKies={(w) => onMethode(w || null)}
        />
      </div>

      <p
        data-meterkast-zin
        style={{
          margin: "13px 0 0",
          fontSize: size.meta,
          color: color.body,
          lineHeight: 1.55,
        }}
      >
        {`${VERGELIJK_LABEL[vergelijk].vraag} `}
        <span style={{ color: color.faint }}>{`Je ziet ${snede(kast.huidig)} naast ${snede(kast.basis)}.`}</span>
      </p>
    </Card>
  );
}

/**
 * De keuzelijst: wat de server gemeten heeft, plus de kant die hij NU toont.
 *
 * Die twee lopen uiteen zodra je op methode vergelijkt: dan zet de server
 * `huidig` op v2 (de nieuwe weging) terwijl `methodes` alleen v1 bevat, want
 * v2 is nog nergens gemeten. Stond die waarde niet in de lijst, dan wees de
 * keuzelijst v1 aan terwijl het scherm v2 laat zien — en dat is precies het
 * misverstand dat de benchmark niet kan hebben.
 */
function opties(
  gemeten: readonly string[],
  nu: string | null,
  tekst: (waarde: string) => string,
): { waarde: string; tekst: string }[] {
  const lijst = gemeten.map((w) => ({ waarde: w, tekst: tekst(w) }));
  if (nu && !gemeten.includes(nu)) {
    lijst.unshift({ waarde: nu, tekst: `${tekst(nu)} — nog niet gemeten` });
  }
  return lijst;
}

/** "methode v1, aug. 2026" — of, zonder periode, wat er wél bekend is. De
 *  server kan een basis zonder periode sturen (er ís geen vorige maand); dan
 *  zegt de zin dat, in plaats van een lege komma. */
function snede(s: { methode: string; period: string | null }): string {
  const m = `methode ${s.methode}`;
  const p = maand(s.period);
  return p ? `${m}, ${p}` : `${m} zonder eerdere periode`;
}

const veldLabel = {
  fontSize: size.label,
  fontWeight: weight.medium,
  color: color.faint,
  letterSpacing: "0.08em",
  textTransform: "uppercase",
} as const;

/** Een keuzelijst met één lege optie: die betekent "laat de server kiezen"
 *  (de nieuwste periode, de standaardmethode) — niet "geen". */
function Kies({
  label: tekst,
  waarde,
  opties,
  onKies,
}: {
  label: string;
  waarde: string;
  opties: readonly { waarde: string; tekst: string }[];
  onKies: (waarde: string) => void;
}) {
  if (opties.length === 0) return null;
  return (
    <label style={{ display: "block" }}>
      <span style={{ ...veldLabel, display: "block", marginBottom: 7 }}>{tekst}</span>
      <select
        value={waarde}
        onChange={(e) => onKies(e.target.value)}
        style={{
          ...knopLicht,
          padding: "7px 10px",
          background: color.surface,
          color: color.ink,
          cursor: "pointer",
        }}
      >
        {opties.map((o) => (
          <option key={o.waarde} value={o.waarde}>
            {o.tekst}
          </option>
        ))}
      </select>
    </label>
  );
}

/** Een herlaadronde die mislukte terwijl er al borden staan: de reden, en dat
 *  wat eronder staat van de vorige keer is. Nooit het hele vlak weg. */
function NietVerversd({ reden, onOpnieuw }: { reden: string; onOpnieuw: () => void }) {
  return (
    <div
      role="alert"
      data-meterkast-fout
      style={{
        display: "flex",
        flexWrap: "wrap",
        alignItems: "center",
        gap: "6px 12px",
        padding: "10px 14px",
        border: `1px solid ${color.line}`,
        borderRadius: radius.block,
        background: color.lineFaint,
        fontSize: size.meta,
        color: color.body,
        lineHeight: 1.5,
      }}
    >
      <span style={{ flex: 1, minWidth: 220 }}>
        {`De meterkast kon niet ververst worden: ${reden} Wat hieronder staat, is van de vorige keer.`}
      </span>
      <button type="button" style={knopLicht} onClick={onOpnieuw}>
        Opnieuw
      </button>
    </div>
  );
}
